"use client";
import { useEffect, useRef, useState } from "react";
import parse from "html-react-parser";

const TYPING_DELAY = 4; // Balanced typing speed
const THINKING_DURATION = 6; // Minimal thinking time
const VARIABLE_DELAY = true; // Add natural variation to typing speed

const isValidHtml = (html: string) => {
  try {
    const template = document.createElement("template");
    template.innerHTML = html;
    return true;
  } catch (e) {
    return false;
  }
};

interface LiveTypingSummaryProps {
  htmlContent: string;
}

const LiveTypingSummary: React.FC<LiveTypingSummaryProps> = ({
  htmlContent,
}) => {
  const summaryRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState(280);
  const [thinking, setThinking] = useState(true);
  const [displayedHtml, setDisplayedHtml] = useState("");
  const [charIndex, setCharIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setThinking(false), THINKING_DURATION);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!thinking && charIndex >= htmlContent.length && summaryRef.current) {
      const contentHeight = summaryRef.current.offsetHeight;
      setMinHeight(contentHeight);
    }
  }, [thinking, charIndex, htmlContent]);

  useEffect(() => {
    if (thinking || !htmlContent || charIndex >= htmlContent.length) return;

    // Create variable delay for more natural typing
    const getVariableDelay = () => {
      if (!VARIABLE_DELAY) return TYPING_DELAY;

      const baseDelay = TYPING_DELAY;
      const variation = Math.random() * 3; // Slightly more variation for natural feel
      const currentChar = htmlContent[charIndex];
      const nextChar = htmlContent[charIndex + 1];

      // Check if we're in a list item or HTML tag
      const contextBefore = htmlContent.slice(
        Math.max(0, charIndex - 50),
        charIndex + 20,
      );
      const isInListItem =
        contextBefore.includes("<li>") || contextBefore.includes("</li>");
      const isInHtmlTag =
        contextBefore.includes("<") && contextBefore.includes(">");
      const isTransitioningListItems =
        contextBefore.includes("</li>") && contextBefore.includes("<li>");
      const isStartingNewListItem =
        contextBefore.includes("</li>") &&
        currentChar === "<" &&
        nextChar === "l";

      // Much faster for HTML tags and list structures
      if (isInHtmlTag || currentChar === "<" || currentChar === ">") {
        return baseDelay + 1; // Slightly longer for HTML tags
      }

      // Special handling for list item transitions - add a brief pause
      if (isTransitioningListItems || isStartingNewListItem) {
        return baseDelay + 6 + variation; // Longer pause between list items for smooth transition
      }

      // Faster for list items content
      if (isInListItem) {
        if (currentChar === "." || currentChar === "!" || currentChar === "?") {
          return baseDelay + 4 + variation; // Slight pause at sentence end
        }
        if (currentChar === "," || currentChar === ";" || currentChar === ":") {
          return baseDelay + 2 + variation; // Brief pause at comma
        }
        if (currentChar === " ") {
          return baseDelay + 1 + variation; // Normal space delay
        }
        return baseDelay + variation;
      }

      // Normal delays for regular text
      if (currentChar === "." || currentChar === "!" || currentChar === "?") {
        return baseDelay + 6 + variation; // Longer pause at sentence end
      }
      if (currentChar === "," || currentChar === ";" || currentChar === ":") {
        return baseDelay + 3 + variation; // Pause at comma
      }
      if (currentChar === " ") {
        return baseDelay + 1 + variation; // Normal space delay
      }

      return baseDelay + variation;
    };

    const timeout = setTimeout(() => {
      const nextIndex = charIndex + 1;
      const nextHtml = htmlContent.slice(0, nextIndex);

      // Find the last complete tag to avoid breaking HTML
      const lastOpenBracket = nextHtml.lastIndexOf("<");
      const lastCloseBracket = nextHtml.lastIndexOf(">");
      const inMiddleOfTag = lastOpenBracket > lastCloseBracket;

      // For list items and common HTML tags, be more lenient with validation
      const isInListStructure =
        nextHtml.includes("<ul>") ||
        nextHtml.includes("<ol>") ||
        nextHtml.includes("<li>");

      if (!inMiddleOfTag && (isValidHtml(nextHtml) || isInListStructure)) {
        setDisplayedHtml(nextHtml);
        setCharIndex(nextIndex);
      } else {
        // If we're in the middle of a tag, just advance by 1 character
        setCharIndex(charIndex + 1);
      }
    }, getVariableDelay());

    return () => clearTimeout(timeout);
  }, [charIndex, htmlContent, thinking]);

  const shouldShowCursor = () => {
    if (!htmlContent || charIndex >= htmlContent.length) {
      return false;
    }
    const currentSlice = htmlContent.slice(0, charIndex);
    const lastOpenBracket = currentSlice.lastIndexOf("<");
    const lastCloseBracket = currentSlice.lastIndexOf(">");
    const inMiddleOfTag = lastOpenBracket > lastCloseBracket;
    if (inMiddleOfTag) {
      return false;
    }
    if (!isValidHtml(currentSlice)) {
      return false;
    }
    if (currentSlice.endsWith(">")) {
      return false;
    }
    if (charIndex < htmlContent.length && htmlContent[charIndex] === "<") {
      return false;
    }
    return true;
  };

  const getHtmlWithCursor = () => {
    if (!shouldShowCursor()) {
      return displayedHtml;
    }
    return displayedHtml + '<span class="typing-cursor">|</span>';
  };

  return (
    <div
      className="typed-summary-container"
      ref={summaryRef}
      style={{ minHeight, transition: "min-height 0.2s ease-out" }}
    >
      {thinking ? (
        <div className="thinking flex animate-pulse items-center gap-1 text-[18px] font-medium text-[#555] transition-all duration-300 ease-in-out">
          🤖 <span>Thinking</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </div>
      ) : (
        <div className="typed-summary transition-all duration-300 ease-in-out">
          {charIndex < htmlContent.length
            ? parse(getHtmlWithCursor())
            : parse(displayedHtml)}
        </div>
      )}
      <style jsx>{`
        .typed-summary-container {
          position: relative;
          display: block;
          width: 100%;
        }
        .typed-summary {
          position: relative;
          font-size: 16px;
          line-height: 1.75;
          color: #000;
          display: block;
          overflow-wrap: break-word;
          word-wrap: break-word;
          opacity: 1;
          transition: opacity 0.2s ease-in-out;
          animation: fadeIn 0.3s ease-in-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .typed-summary :global(ul) {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .typed-summary :global(li) {
          margin-bottom: 0.4rem;
          display: list-item;
        }
        .typed-summary :global(p) {
          margin-bottom: 0.5rem;
        }
        .typed-summary :global(h1),
        .typed-summary :global(h2),
        .typed-summary :global(h3),
        .typed-summary :global(h4),
        .typed-summary :global(h5),
        .typed-summary :global(h6) {
          font-weight: bold;
          margin-bottom: 0.5rem;
          margin-top: 1rem;
        }
        .typed-summary :global(h1) {
          font-size: 1.5em;
        }
        .typed-summary :global(h2) {
          font-size: 1.3em;
        }
        .typed-summary :global(h3) {
          font-size: 1.1em;
        }
        .typed-summary :global(strong) {
          font-weight: bold;
        }
        .typed-summary :global(em) {
          font-style: italic;
        }
        .typing-cursor {
          display: inline;
          font-weight: 400;
          color: #000;
          font-size: 16px;
          line-height: 1.75;
          animation: cursor-blink 0.8s ease-in-out 0s infinite;
          margin: 0 1px;
          vertical-align: baseline;
          opacity: 1;
          transition: opacity 0.1s ease;
        }
        @keyframes cursor-blink {
          0%,
          40% {
            opacity: 1;
          }
          50%,
          90% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .dot {
          animation: dot-blink 1s infinite;
        }
        .dot:nth-child(2) {
          animation-delay: 0.15s;
        }
        .dot:nth-child(3) {
          animation-delay: 0.3s;
        }
        @keyframes dot-blink {
          0%,
          20% {
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default LiveTypingSummary;
